import {
  ArrowDownTrayIcon,
  GlobeAltIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { riskSeveritySortOrder } from '@/app/constant';
import {
  Agents as AgentsIcon,
  Assets as AssetsIcon,
  Button,
  RiskTag,
  Seeds as SeedsIcon,
  TagSeverity,
  Vulnerabilities as VulnerabilitiesIcon,
} from '@/components/chariot-ui';
import { Loader } from '@/components/Loader';
import { Popover } from '@/components/Popover';
import { NoData } from '@/components/ui/NoData';
import { useDownloadFile } from '@/hooks';
import { useGetDownloads } from '@/hooks/download';
import { Job, JobStatus, RiskSeverity, SeverityDef } from '@/types';
import { formatDate } from '@/utils/date.util';

interface Download {
  type: 'asset' | 'seed' | 'risk' | 'technology' | 'agent';
  key: string;
  processing: boolean;
  updated: string;
  riskSeverities: RiskSeverity[];
  format: string;
}

export function GlobalDownload() {
  const [open, setOpen] = useState(false);

  const { mutateAsync: downloadFile } = useDownloadFile();

  const { data: exportingFileJobs, status: exportingFileJobsStatus } =
    useGetDownloads({ enabled: open });

  const { recentDownloads, downloadsExist } = useMemo(() => {
    const updatedRecentDownloads = exportingFileJobs
      .filter(job => {
        const status = job.status.toString();
        const hasValidStatus =
          status.startsWith(JobStatus.Queued) ||
          status.startsWith(JobStatus.Running) ||
          status.startsWith(JobStatus.Pass);
        return hasValidStatus && getJobType(job);
      })
      .map(job => {
        const type = getJobType(job);
        const isRisk = type === 'risk';
        const outputPath = getOutputPath(job);

        const riskQueryFromKey = isRisk
          ? job.key.match(/(?<=status:)O\w?/g)
          : undefined;

        const exportedRiskSeverities: RiskSeverity[] = isRisk
          ? ((riskQueryFromKey
              ?.map(query => query[1])
              .filter(severity => severity) || []) as RiskSeverity[])
          : [];

        return {
          key: outputPath,
          processing:
            job.status.toString().startsWith(JobStatus.Running) ||
            job.status.toString().startsWith(JobStatus.Queued),
          updated: job.updated,
          type,
          riskSeverities: exportedRiskSeverities.sort((a, b) => {
            return (
              riskSeveritySortOrder.indexOf(a) -
              riskSeveritySortOrder.indexOf(b)
            );
          }),
          format: outputPath.endsWith('.json') ? 'json' : 'csv',
        } as Download;
      })
      .sort(
        (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime()
      );

    return {
      recentDownloads: updatedRecentDownloads,
      downloadsExist: updatedRecentDownloads.length > 0,
    };
  }, [exportingFileJobs]);

  return (
    <Popover
      open={open}
      setOpen={setOpen}
      className="overflow-auto p-0"
      placement="bottom"
      allowFlip={false}
      container={
        <div>
          <Button
            styleType="icon"
            className="hover:bg-transparent"
            startIcon={
              <ArrowDownTrayIcon className="text-chariot-brand-secondary size-8 cursor-pointer select-none" />
            }
            onClick={() => {}}
            label=""
          />
        </div>
      }
    >
      <div className="flex w-[350px] flex-col">
        <Loader
          isLoading={exportingFileJobsStatus === 'pending'}
          type="spinner"
          className="h-48"
        >
          {!downloadsExist && (
            <NoData
              icon={
                <ArrowDownTrayIcon className="stroke-chariot-text-primary size-14" />
              }
              title="No recent exports"
            />
          )}
          {recentDownloads.map((download, index) => (
            <Fragment key={`download-${index}`}>
              <div className="flex flex-col p-4">
                <div className="flex items-center gap-2 pb-2">
                  {download.type === 'asset' && (
                    <AssetsIcon className="stroke-chariot-text-primary size-4" />
                  )}
                  {download.type === 'seed' && (
                    <SeedsIcon className="fill-chariot-text-primary size-4" />
                  )}
                  {download.type === 'risk' && (
                    <VulnerabilitiesIcon className="fill-chariot-text-primary size-4" />
                  )}
                  {download.type === 'technology' && (
                    <GlobeAltIcon className="size-5" />
                  )}
                  {download.type === 'agent' && (
                    <AgentsIcon className="size-4" />
                  )}

                  <p className="text-bold flex-1 text-sm">
                    {download.type === 'asset' && 'Assets'}
                    {download.type === 'seed' && 'Seeds'}
                    {download.type === 'risk' && 'Vulnerabilities'}
                    {download.type === 'technology' && 'Technologies'}
                    {download.type === 'agent' && 'Agents'}
                  </p>
                </div>
                {download.processing ? (
                  <div className="flex items-center gap-2 text-xs">
                    Processing...
                    <Loader isLoading type="spinner" className="size-4" />
                  </div>
                ) : (
                  <>
                    <p className="pb-2 text-xs">
                      Exported {formatDate(download.updated)}
                    </p>
                    {download.type === 'risk' && (
                      <>
                        {download.riskSeverities.length === 0 && (
                          <div className="pb-2 text-xs font-bold italic">
                            All Open Vulnerabilities
                          </div>
                        )}
                        {download.riskSeverities.length > 0 && (
                          <div className="flex flex-wrap items-center gap-2 pb-2">
                            {download.riskSeverities?.map((severity, index) => (
                              <>
                                <RiskTag
                                  size="xs"
                                  severity={
                                    SeverityDef[
                                      severity
                                    ]?.toLowerCase() as TagSeverity
                                  }
                                  key={severity}
                                />
                                {index < download.riskSeverities.length - 1 && (
                                  <PlusIcon
                                    className="size-4 stroke-2"
                                    key={`${severity}-plusIcon`}
                                  />
                                )}
                              </>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    <Button
                      className="w-fit"
                      label={
                        download.format === 'csv'
                          ? 'Download CSV File'
                          : 'Download JSON File'
                      }
                      styleType="rounded"
                      size="xs"
                      onClick={() => {
                        if (index === 0) {
                          toast.dismiss();
                        }

                        downloadFile({
                          name: download.key,
                          downloadName: download.key.replace('home/', ''),
                        });
                      }}
                    />
                  </>
                )}
              </div>
              {index < recentDownloads.length - 1 && (
                <div className="w-[calc(100% - 34px)] bg-chariot-stroke mx-4 h-[2px] shrink-0" />
              )}
            </Fragment>
          ))}
        </Loader>
      </div>
    </Popover>
  );
}

function getJobConfig(job: Job): Record<string, string> | undefined {
  return job.config as Record<string, string> | undefined;
}

function getOutputPath(job: Job): string {
  const config = getJobConfig(job);
  return config?.output ?? job.dns;
}

function getJobType(
  job: Job
): 'asset' | 'seed' | 'risk' | 'technology' | 'agent' | '' {
  const config = getJobConfig(job);
  const label = config?.label;

  // New capability-based exports store the label in config
  if (label) {
    switch (label) {
      case 'asset':
      case 'seed':
      case 'risk':
      case 'technology':
      case 'agent':
        return label;
      default:
        return '';
    }
  }

  // Fall back to parsing the DNS/output path (legacy plextrac exports)
  const outputPath = getOutputPath(job);
  if (outputPath.startsWith('home/export-asset')) return 'asset';
  if (outputPath.startsWith('home/export-seed')) return 'seed';
  if (outputPath.startsWith('home/export-risk')) return 'risk';
  if (outputPath.startsWith('home/export-technology')) return 'technology';
  if (outputPath.startsWith('home/export-agent')) return 'agent';
  return '';
}
